import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * 🎯 Hook para gerenciar estado dos menus/submenus da sidebar
 * - Controla qual menu está aberto/fechado
 * - Mantém estado ativo baseado na rota atual
 * - Persiste estado no localStorage
 */
export const useMenuState = () => {
  const location = useLocation();
  const [openMenus, setOpenMenus] = useState(() => {
    // Recuperar estado do localStorage
    const saved = localStorage.getItem('pagmus-open-menus');
    return saved ? JSON.parse(saved) : {};
  });

  // 📋 Definir estrutura de menus e suas rotas
  // NOTA: itens (V2) removidos pra eliminar duplicidade. As telas V1 vão usar
  // backend V2 in-place (ver docs/V2/PLANO_REVERSAO_FRONTEND_V2.md).
  const menuStructure = {
    produtos: {
      label: 'Produtos',
      icon: 'mdi:package-variant',
      routes: [
        '/products/registration/new',
        '/products/products',
        '/products/affiliates',
        '/manager/products',  // Rota para gerentes de afiliados (lista)
        '/manager/products/', // Rota para detalhes do gerente (dinâmica)
        '/products/seller'    // Rota específica para sellers
      ],
      submenus: [
        { path: '/products/registration/new', label: 'Cadastrar Produto', icon: 'mdi:plus-circle' },
        { path: '/products/products', label: 'Meus Produtos', icon: 'mdi:view-list' },
        { path: '/products/affiliates', label: 'Produtos que Promovo', icon: 'mdi:account-network' },
        { path: '/manager/products', label: 'Produtos que Gerencio', icon: 'mdi:account-tie' },
        { path: '/products/seller', label: 'Produtos', icon: 'mdi:package-variant' }  // Para sellers
      ]
    },
    vendas: {
      label: 'Vendas',
      icon: 'mdi:chart-line',
      routes: [
        '/sales-management',
        '/afterpay',
        '/commissions/dashboard'
      ],
      submenus: [
        { path: '/sales-management', label: 'Relatório de Vendas', icon: 'mdi:chart-line' },
        { path: '/afterpay', label: 'Pedidos de AfterPay', icon: 'mdi:cog' },
        { path: '/commissions/dashboard', label: 'Minhas Comissões', icon: 'mdi:chart-line' }
      ]
    },
    loja: {
      label: 'Loja',
      icon: 'mdi:store',
      routes: [
        '/store/store-products'
      ],
      submenus: [
        { path: '/store/store-products', label: 'Loja', icon: 'mdi:store' }
      ]
    },
    compras: {
      label: 'Compras',
      icon: 'mdi:shopping',
      routes: [
        '/my-purchases',
        '/purchases/status'
      ],
      submenus: [
        { path: '/my-purchases', label: 'Minhas Compras', icon: 'mdi:shopping' },
        { path: '/purchases/status', label: 'Acompanhar Entrega', icon: 'mdi:truck-delivery' }
      ]
    },
    afiliados: {
      label: 'Afiliados',
      icon: 'mdi:account-network',
      routes: [
        '/my-affiliates',
        '/my-affiliate-requests'
      ],
      submenus: [
        { path: '/my-affiliates', label: 'Meus Afiliados', icon: 'mdi:account-group' },
        { path: '/my-affiliate-requests', label: 'Minhas Solicitações', icon: 'mdi:account-clock' }
      ]
    },
    financeiro: {
      label: 'Financeiro',
      icon: 'mdi:finance',
      routes: [
        '/withdrawals',
        '/banks',
        '/responsibles'
      ],
      submenus: [
        { path: '/withdrawals', label: 'Saques', icon: 'mdi:cash' },
        { path: '/banks', label: 'Contas Bancárias', icon: 'mdi:bank' },
        { path: '/responsibles', label: 'Responsáveis', icon: 'mdi:account-tie' }
      ]
    },
    relatorios: {
      label: 'Relatórios',
      icon: 'mdi:chart-box',
      routes: [
        '/reports/executive',
        '/reports/sales',
        '/reports/commissions',
        '/reports/affiliate-performance',
        '/reports/products',
        '/reports/afterpay',
        '/reports/clients',
        '/reports/financial',
        '/reports/coupons',
        '/reports/churn',
        '/reports/abandonments',
        '/reports/refunds',
        '/reports/teams'
      ],
      submenus: [
        { path: '/reports/executive', label: 'Dashboard Executivo', icon: 'mdi:view-dashboard' },
        { path: '/reports/sales', label: 'Vendas & Comissões', icon: 'mdi:cart' },
        { path: '/reports/commissions', label: 'Comissões Detalhadas', icon: 'mdi:account-cash' },
        { path: '/reports/affiliate-performance', label: 'Performance Afiliados', icon: 'mdi:account-group' },
        { path: '/reports/products', label: 'Produtos', icon: 'mdi:package-variant' },
        { path: '/reports/afterpay', label: 'AfterPay / Logística', icon: 'mdi:truck-delivery' },
        { path: '/reports/clients', label: 'Clientes', icon: 'mdi:account-group' },
        { path: '/reports/financial', label: 'Financeiro', icon: 'mdi:currency-usd' },
        { path: '/reports/coupons', label: 'Cupons', icon: 'mdi:ticket-percent' },
        { path: '/reports/churn', label: 'Churn Rate', icon: 'mdi:account-minus' },
        { path: '/reports/abandonments', label: 'Abandonos', icon: 'mdi:cart-remove' },
        { path: '/reports/refunds', label: 'Estornos', icon: 'mdi:undo-variant' },
        { path: '/reports/teams', label: 'Times & Perfis', icon: 'mdi:account-group' }
      ]
    },
    // 📊 VENDAS (ADMIN)
    adminVendas: {
      label: 'Vendas',
      icon: 'mdi:chart-line',
      routes: ['/admin/orders'],
      submenus: [
        { path: '/admin/orders', label: 'Pedidos', icon: 'mdi:clipboard-list' }
      ]
    },
    // 💰 FINANCEIRO (ADMIN)
    adminFinanceiro: {
      label: 'Financeiro',
      icon: 'mdi:cash-multiple',
      routes: ['/admin/withdrawals'],
      submenus: [
        { path: '/admin/withdrawals', label: 'Solicitações de Saque', icon: 'mdi:account-supervisor' }
      ]
    },
    // ✅ APROVAÇÕES (ADMIN)
    adminAprovacoes: {
      label: 'Aprovações',
      icon: 'mdi:check-circle',
      routes: ['/admin/approvals', '/admin/approve-products', '/admin/bank-accounts'],
      submenus: [
        { path: '/admin/approvals', label: 'Aprovar Responsável', icon: 'mdi:file-document-check' },
        { path: '/admin/approve-products', label: 'Aprovar Produto', icon: 'mdi:package-check' },
        { path: '/admin/bank-accounts', label: 'Aprovar Conta Bancária', icon: 'mdi:bank-check' }
      ]
    },
    // 🆘 SUPORTE (ADMIN)
    adminSuporte: {
      label: 'Suporte',
      icon: 'mdi:lifebuoy',
      routes: ['/admin/tickets'],
      submenus: [
        { path: '/admin/tickets', label: 'Helpdesk', icon: 'mdi:headset' }
      ]
    },
    // 👥 USUÁRIOS (ADMIN)
    adminUsuarios: {
      label: 'Usuários',
      icon: 'mdi:account-group',
      routes: ['/admin/users', '/admin/customers', '/admin/permissions'],
      submenus: [
        { path: '/admin/users', label: 'Produtores/Afiliados', icon: 'mdi:account-group' },
        { path: '/admin/customers', label: 'Clientes', icon: 'mdi:account-multiple' },
        // Pacote 6 — Sub-perfis admin granulares
        { path: '/admin/permissions', label: 'Sub-perfis Admin', icon: 'mdi:shield-account-outline' }
      ]
    },
    // ⚙️ CONFIGURAÇÕES (ADMIN)
    adminConfiguracoes: {
      label: 'Configurações',
      icon: 'mdi:cog',
      routes: ['/admin/config', '/admin/platform-fee-plans', '/admin/advance-payment-plans', '/admin/minimum-withdrawal-plans'],
      submenus: [
        { path: '/admin/platform-fee-plans', label: 'Taxa da Plataforma', icon: 'mdi:cash-multiple' },
        { path: '/admin/advance-payment-plans', label: 'Planos de Antecipação', icon: 'mdi:rocket-launch' },
        { path: '/admin/config', label: 'Períodos de Liberação', icon: 'mdi:calendar-clock' },
        { path: '/admin/minimum-withdrawal-plans', label: 'Valor Mínimo de Saque', icon: 'mdi:cash-check' }
      ]
    },
    acessos: {
      label: 'Acessos',
      icon: 'mdi:shield-account',
      routes: [
        '/team',
        '/permission-groups'
      ],
      submenus: [
        { path: '/team', label: 'Usuários', icon: 'mdi:account-supervisor' },
        { path: '/permission-groups', label: 'Grupos de Permissões', icon: 'mdi:account-group' }
      ]
    },
    operacoes: {
      label: 'Operações',
      icon: 'mdi:tools',
      routes: [
        '/api',
        '/webhook',
        '/webhooks',
        '/integration/bling',
        '/integration/notazz'
      ],
      submenus: [
        { path: '/api', label: 'API', icon: 'mdi:api' },
        { path: '/webhook', label: 'Postback', icon: 'mdi:webhook' },
        { path: '/webhooks', label: 'Webhooks', icon: 'mdi:webhook' },
        { path: '/integration/bling', label: 'Bling ERP', icon: 'mdi:package-variant-closed' },
        { path: '/integration/notazz', label: 'Notazz (NF)', icon: 'mdi:file-document' }
      ]
    },
    ajuda: {
      label: 'Ajuda',
      icon: 'mdi:help-circle',
      routes: [
        '/wiki',
        '/tickets'
      ],
      submenus: [
        { path: '/wiki', label: 'Pagmus Wiki', icon: 'mdi:book-open-page-variant' },
        { path: '/tickets', label: 'Tickets de Suporte', icon: 'mdi:ticket-account' }
      ]
    }
  };

  // 🔍 Função para encontrar qual menu contém a rota atual
  const findActiveMenu = (currentPath) => {
    for (const [menuKey, menuData] of Object.entries(menuStructure)) {
      if (menuData.routes.some(route => currentPath.startsWith(route))) {
        return menuKey;
      }
    }
    return null;
  };

  // 🔍 Função para verificar se uma rota está ativa
  const isRouteActive = (path) => {
    return location.pathname === path;
  };

  // 🔍 Função para verificar se um menu tem alguma rota ativa
  const isMenuActive = (menuKey) => {
    const menu = menuStructure[menuKey];
    return menu.routes.some(route => location.pathname.startsWith(route));
  };

  // 🔄 Função para alternar estado de um menu
  // Quando expandir um menu, recolher todos os outros
  const toggleMenu = (menuKey) => {
    setOpenMenus(prev => {
      const isCurrentlyOpen = prev[menuKey];
      
      // Se está abrindo, fechar todos os outros
      // Se está fechando, apenas fechar este
      const newState = isCurrentlyOpen 
        ? { ...prev, [menuKey]: false }  // Fechando: apenas este
        : { [menuKey]: true };            // Abrindo: apenas este aberto
      
      // Salvar no localStorage
      localStorage.setItem('pagmus-open-menus', JSON.stringify(newState));
      
      return newState;
    });
  };

  // 🎯 Auto-abrir menu que contém a rota ativa
  useEffect(() => {
    const activeMenu = findActiveMenu(location.pathname);
    if (activeMenu && !openMenus[activeMenu]) {
      setOpenMenus(prev => {
        const newState = {
          ...prev,
          [activeMenu]: true
        };
        localStorage.setItem('pagmus-open-menus', JSON.stringify(newState));
        return newState;
      });
    }
  }, [location.pathname]);

  return {
    menuStructure,
    openMenus,
    toggleMenu,
    isRouteActive,
    isMenuActive,
    findActiveMenu: () => findActiveMenu(location.pathname)
  };
};
